import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';
import { useData } from '@hooks/useData';

export type RegistrationDocumentUploadInput = {
    documents: { uuid: string, type: string; file: File }[];
    language?: string;
};

export type RegistrationDocumentStepSuccessResponse = {
    success: boolean;
    message: string;
    documents: {
        CC: string;
        PASS: string;
        CNH: string;
    };
};

interface ExistingDocument {
    uuid: string;
    document_type: string;
    document_name: string;
    link: string;
}


export const useDocumentsUpload = () => {
    const { handleCommonErrors } = useApiConfig();
    const { login } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    // for errors associated with form fields (422 Unprocessable Content)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [validationErrors, setValidationErrors] = useState<any | null>(null);
    const [documents, setDocuments] = useState<Record<string, ExistingDocument>>({});
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (props: RegistrationDocumentUploadInput) => {
        setLoading(true);
        setError(null);
        setValidationErrors(null);

        try {
            const formData = new FormData();
            if (props.language) formData.append('language', props.language);

            props.documents.forEach((doc, index) => {
                formData.append(`documents[${index}][uuid]`, doc.uuid);
                formData.append(`documents[${index}][type]`, doc.type);
                formData.append(`documents[${index}][file]`, doc.file);
            });

            const headers: HeadersInit = {};
            if (login?.data?.access_token) {
                headers.Authorization = `Bearer ${login.data.access_token}`;
            }

            const response = await fetch(`${API_URL}/user-documents/update-documents`, {
                method: 'POST',
                body: formData,
                headers: headers,
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;
                throw error;
            }
            const rawResponse = await response.json();
            const data = rawResponse.data;

            setSuccess(rawResponse.message);
            setDocuments(rawResponse.data);
            return data;

        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, success, validationErrors, lastResponseTime, documents };
};
